const SERVER_ERROR_TEXT = {
  SOMETHING_WENT_WRONG_REQUEST: 'Something went wrong with your request.',
  SESSION_EXPIRED: 'Your session has expired. Please log in again.',
  NO_PERMISSION: 'You do not have permission to perform this action.',
  RESOURCE_NOT_FOUND: 'The requested resource could not be found.',
  REQUEST_TIMEOUT: 'The request timed out. Please try again.',
  CONFLICT: 'The request could not be completed due to a conflict.',
  VALIDATION_FAILED: 'Validation failed. Please check your input.',
  TOO_MANY_REQUESTS: 'Too many requests. Please wait and try again later.',
  SERVER_SIDE_ERROR: 'Something went wrong.',
  SERVICE_TEMPORARILY_UNAVAILABLE: 'Service is temporarily unavailable.'
};

const ERROR_MESSAGE_TEXT = {
  RESOURCE_CANNOT_BE_DELETED_IN_USE: 'Resource cannot be deleted because it is in use',
  RESOURCE_CANNOT_BE_DELETED: 'Resource cannot be deleted'
};

export enum HttpStatusCode {
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  REQUEST_TIMEOUT = 408,
  CONFLICT = 409,
  UNPROCESSABLE_ENTITY = 422,
  TOO_MANY_REQUESTS = 429,
  INTERNAL_SERVER_ERROR = 500,
  BAD_GATEWAY = 502,
  SERVICE_UNAVAILABLE = 503,
  GATEWAY_TIMEOUT = 504
}

export const HTTP_STATUS = {
  OK: 200,
  UNKNOWN: 0
};

export const REST_API_FAILURE_HTTP_STATUS = HttpStatusCode.BAD_REQUEST;

export const API_RESPONSE_MESSAGE_KEY = 'message';

export const STRING_TYPE = 'string';

export const ERROR_MESSAGE_SEPARATOR = {
  MULTI: ', ',
  FIELD: ' '
};

export const INVALID_API_TOKEN_ERROR_MESSAGE = 'Invalid X-API-TOKEN';
export const DEFAULT_SERVER_ERROR_MESSAGE = 'Unexpected error occurred.';
export const SUPER_USER_CHECK_FAILED_MESSAGE = 'Super user check failed';

export const SERVER_ERROR_MESSAGES: Record<HttpStatusCode, string> = {
  [HttpStatusCode.BAD_REQUEST]: SERVER_ERROR_TEXT.SOMETHING_WENT_WRONG_REQUEST,
  [HttpStatusCode.UNAUTHORIZED]: SERVER_ERROR_TEXT.SESSION_EXPIRED,
  [HttpStatusCode.FORBIDDEN]: SERVER_ERROR_TEXT.NO_PERMISSION,
  [HttpStatusCode.NOT_FOUND]: SERVER_ERROR_TEXT.RESOURCE_NOT_FOUND,
  [HttpStatusCode.REQUEST_TIMEOUT]: SERVER_ERROR_TEXT.REQUEST_TIMEOUT,
  [HttpStatusCode.CONFLICT]: SERVER_ERROR_TEXT.CONFLICT,
  [HttpStatusCode.UNPROCESSABLE_ENTITY]: SERVER_ERROR_TEXT.VALIDATION_FAILED,
  [HttpStatusCode.TOO_MANY_REQUESTS]: SERVER_ERROR_TEXT.TOO_MANY_REQUESTS,
  [HttpStatusCode.INTERNAL_SERVER_ERROR]: SERVER_ERROR_TEXT.SERVER_SIDE_ERROR,
  [HttpStatusCode.BAD_GATEWAY]: SERVER_ERROR_TEXT.SERVICE_TEMPORARILY_UNAVAILABLE,
  [HttpStatusCode.SERVICE_UNAVAILABLE]: SERVER_ERROR_TEXT.SERVICE_TEMPORARILY_UNAVAILABLE,
  [HttpStatusCode.GATEWAY_TIMEOUT]: SERVER_ERROR_TEXT.SERVICE_TEMPORARILY_UNAVAILABLE
};

export const MAINTENANCE_ERROR_STATUSES = new Set<number>([
  HttpStatusCode.BAD_GATEWAY,
  HttpStatusCode.SERVICE_UNAVAILABLE
]);

export const InterceptorToastMessages = {
  ErrorWhileGettingRedirectionUrl: 'Error while getting redirection URL'
};

export const ERROR_EXCEPTION_CODES = {
  NO_X_API_TOKEN: 'E001',
  INVALID_TOKEN: 'E001.1',
  EXPIRED_ONE_TIME_TOKEN: 'E001.2',
  INVALID_X_API_TOKEN: 'E002',
  NO_OBJECT_FOUND: 'E003',
  MULTIPLE_OBJECTS_RETURNED: 'E004',
  TOOLSET_ACCESS_NOT_ALLOWED: 'E005',
  INVALID_X_SERVICE_TOKEN: 'E006',
  NO_X_SERVICE_TOKEN: 'E007',
  NO_X_ORG_ID_OR_ACCESS_DENIED: 'E008',
  ORG_NAME_ALREADY_USED: 'E008.1',
  INVALID_ORG_ID_DB_NOT_CONFIGURED: 'E008_a',
  DATABASE_CONNECTION_FAILED: 'E008_b',
  WORK_ORDER_DELETION_NOT_ALLOWED: 'E009',
  PIPELINE_STEP_DELETION_NOT_ALLOWED: 'E010',
  ORDER_BY_REQUIRED: 'E011',
  INVALID_ORDER_BY_FIELD: 'E012',
  WORK_ORDER_SCHEDULE_DELETION_NOT_ALLOWED: 'E013',
  USER_NOT_FOUND: 'E014',
  LINKED_COMPONENT_DELETION_NOT_ALLOWED: 'E014_LINKED_COMPONENT',
  INVALID_OR_EXPIRED_TOKEN: 'E015',
  SOME_ERROR: 'E00X',
  USER_ALREADY_OWNS_ORGANIZATION: 'ORG001',
  ACTIVE_USER_NOT_FOUND: 'USER001',
  SUPER_USER_NOT_FOUND: 'USER002',
  USER_NOT_PART_OF_ORGANIZATION: 'USER003',
  ACCESS_NOT_ALLOWED_FOR_USER: 'USER004',
  USER_ALREADY_EXISTS: 'USER005',
  USER_ALREADY_IN_ORGANIZATION: 'USER006',
  UNIQUE_CONSTRAINT_ERROR: 'V001',
  MISSING_QUERY_PARAMETER: 'V002',
  APP_CONFIG_NOT_FOUND: 'V003',
  WEBFLOW_WEBHOOK_TIMESTAMP_EXPIRED: 'WH001',
  WEBFLOW_WEBHOOK_INVALID_SIGNATURE: 'WH002',
  WEBFLOW_WEBHOOK_MISSING_SIGNATURE: 'WH003',
  WEBFLOW_WEBHOOK_MISSING_TIMESTAMP: 'WH004',
  BUDGET_DELETION_NOT_ALLOWED: 'BIDS001',
  TODO_LIST_NOT_FOUND: 'TODO003',
  NO_TODOS_TO_REORDER: 'TODO004'
};

export const RUNTIME_MESSAGE_PREFERRED_CODES = new Set<string>([
  ERROR_EXCEPTION_CODES.NO_X_ORG_ID_OR_ACCESS_DENIED,
  ERROR_EXCEPTION_CODES.SOME_ERROR
]);

export const toErrorMessageLookupKey = (code: string, status?: number): string =>
  status === undefined ? code : `${code}:${status}`;

export const ERROR_CODE_MESSAGES: Readonly<Record<string, string>> = {
  [ERROR_EXCEPTION_CODES.NO_X_API_TOKEN]: 'Required authentication token is missing',
  [ERROR_EXCEPTION_CODES.INVALID_TOKEN]: 'Authentication token is invalid',
  [ERROR_EXCEPTION_CODES.EXPIRED_ONE_TIME_TOKEN]: 'Authentication token has expired',
  [ERROR_EXCEPTION_CODES.INVALID_X_API_TOKEN]: 'API token is invalid',
  [ERROR_EXCEPTION_CODES.NO_OBJECT_FOUND]: 'Requested resource not found',
  [ERROR_EXCEPTION_CODES.MULTIPLE_OBJECTS_RETURNED]: 'Multiple resources found',
  [ERROR_EXCEPTION_CODES.TOOLSET_ACCESS_NOT_ALLOWED]: 'Access to the requested resource is not allowed',
  [ERROR_EXCEPTION_CODES.INVALID_X_SERVICE_TOKEN]: 'Service token is invalid',
  [ERROR_EXCEPTION_CODES.NO_X_SERVICE_TOKEN]: 'Required service token is missing',
  [ERROR_EXCEPTION_CODES.NO_X_ORG_ID_OR_ACCESS_DENIED]: 'Organization access denied',
  [ERROR_EXCEPTION_CODES.ORG_NAME_ALREADY_USED]: 'Organization name is already in use',
  [ERROR_EXCEPTION_CODES.INVALID_ORG_ID_DB_NOT_CONFIGURED]: 'Organization is not configured',
  [ERROR_EXCEPTION_CODES.DATABASE_CONNECTION_FAILED]: 'Failed to connect to the database',
  [ERROR_EXCEPTION_CODES.WORK_ORDER_DELETION_NOT_ALLOWED]: ERROR_MESSAGE_TEXT.RESOURCE_CANNOT_BE_DELETED_IN_USE,
  [ERROR_EXCEPTION_CODES.PIPELINE_STEP_DELETION_NOT_ALLOWED]: ERROR_MESSAGE_TEXT.RESOURCE_CANNOT_BE_DELETED_IN_USE,
  [ERROR_EXCEPTION_CODES.ORDER_BY_REQUIRED]: 'Required sorting parameter is missing',
  [ERROR_EXCEPTION_CODES.INVALID_ORDER_BY_FIELD]: 'Invalid sorting parameter',
  [ERROR_EXCEPTION_CODES.WORK_ORDER_SCHEDULE_DELETION_NOT_ALLOWED]:
    ERROR_MESSAGE_TEXT.RESOURCE_CANNOT_BE_DELETED_IN_USE,
  [toErrorMessageLookupKey(ERROR_EXCEPTION_CODES.USER_NOT_FOUND, HttpStatusCode.NOT_FOUND)]: 'User account not found',
  [toErrorMessageLookupKey(ERROR_EXCEPTION_CODES.USER_NOT_FOUND, HttpStatusCode.CONFLICT)]:
    ERROR_MESSAGE_TEXT.RESOURCE_CANNOT_BE_DELETED_IN_USE,
  [ERROR_EXCEPTION_CODES.LINKED_COMPONENT_DELETION_NOT_ALLOWED]: ERROR_MESSAGE_TEXT.RESOURCE_CANNOT_BE_DELETED_IN_USE,
  [ERROR_EXCEPTION_CODES.INVALID_OR_EXPIRED_TOKEN]: 'Session token is invalid',
  [ERROR_EXCEPTION_CODES.SOME_ERROR]: 'An unexpected error occurred',
  [ERROR_EXCEPTION_CODES.USER_ALREADY_OWNS_ORGANIZATION]:
    'You already own an organization. You cannot create another one.',
  [ERROR_EXCEPTION_CODES.ACTIVE_USER_NOT_FOUND]: 'Active user not found',
  [ERROR_EXCEPTION_CODES.SUPER_USER_NOT_FOUND]: 'Super user not found',
  [ERROR_EXCEPTION_CODES.USER_NOT_PART_OF_ORGANIZATION]: 'User is not authorized for the requested organization',
  [ERROR_EXCEPTION_CODES.ACCESS_NOT_ALLOWED_FOR_USER]: 'User does not have sufficient permissions',
  [ERROR_EXCEPTION_CODES.USER_ALREADY_EXISTS]: 'User already exists',
  [ERROR_EXCEPTION_CODES.USER_ALREADY_IN_ORGANIZATION]: 'User is already a member of the organization',
  [ERROR_EXCEPTION_CODES.UNIQUE_CONSTRAINT_ERROR]: 'Resource already exists',
  [ERROR_EXCEPTION_CODES.MISSING_QUERY_PARAMETER]: 'Required query parameter is missing',
  [ERROR_EXCEPTION_CODES.APP_CONFIG_NOT_FOUND]: 'Application configuration not found',
  [ERROR_EXCEPTION_CODES.WEBFLOW_WEBHOOK_TIMESTAMP_EXPIRED]: 'Request timestamp has expired',
  [ERROR_EXCEPTION_CODES.WEBFLOW_WEBHOOK_INVALID_SIGNATURE]: 'Webhook signature is invalid',
  [ERROR_EXCEPTION_CODES.WEBFLOW_WEBHOOK_MISSING_SIGNATURE]: 'Required webhook signature is missing',
  [ERROR_EXCEPTION_CODES.WEBFLOW_WEBHOOK_MISSING_TIMESTAMP]: 'Required webhook timestamp is missing',
  [ERROR_EXCEPTION_CODES.BUDGET_DELETION_NOT_ALLOWED]: ERROR_MESSAGE_TEXT.RESOURCE_CANNOT_BE_DELETED,
  [ERROR_EXCEPTION_CODES.TODO_LIST_NOT_FOUND]: 'Todo list not found',
  [ERROR_EXCEPTION_CODES.NO_TODOS_TO_REORDER]: 'No Todos found to reorder'
};
